import Rollbar from 'rollbar';

import { config } from './config';

// Mirror the backend scrub list (errorReporting.service.ts) plus common browser-side fields.
const SCRUB_FIELDS = [
    'Authorization',
    'authorization',
    'password',
    'oldPassword',
    'newPassword',
    'token',
    'socialToken',
    'creditCard',
    // Anonymous routes carry no `person`, so an address in the URL would be the only identifying field.
    'email',
    'prefillEmail',
    'redirect',
];

const SCRUB_HEADERS = ['Authorization'];

const IGNORED_MESSAGES = [/ResizeObserver loop/i, /Script error\.?/i];

function isExpectedError(_isUncaught: boolean, _args: Rollbar.LogArgument[], payload: object): boolean {
    const body = (payload as Record<string, unknown>)?.['body'] as Record<string, unknown> | undefined;
    const message =
        (body?.['message'] as Record<string, string>)?.['body'] ??
        (body?.['trace'] as Record<string, Record<string, string>>)?.['exception']?.['message'] ??
        '';

    for (const pattern of IGNORED_MESSAGES) {
        if (pattern.test(message)) {
            return true;
        }
    }

    if (/validation failed/i.test(message)) {
        return true;
    }

    return false;
}

export type RollbarPerson = {
    id: string;
    email: string;
    partnerUuid?: string;
    impersonatorEmail?: string;
};

type RollbarTrace = { frames?: { filename?: string }[] };
type RollbarBody = { trace?: RollbarTrace; trace_chain?: RollbarTrace[] };

const ROLLBAR_DYNAMIC_HOST = 'dynamichost';

let resolvePerson: () => RollbarPerson | undefined = () => undefined;

export function setRollbarPersonResolver(resolver: () => RollbarPerson | undefined): void {
    resolvePerson = resolver;
}

export function toDynamicHostFilename(filename: string, location: Pick<Location, 'origin' | 'protocol'>): string {
    return filename.startsWith(`${location.origin}/`)
        ? `${location.protocol}//${ROLLBAR_DYNAMIC_HOST}${filename.slice(location.origin.length)}`
        : filename;
}

export function prepareRollbarItem(data: Rollbar.Dictionary): void {
    const person = resolvePerson();
    if (person) {
        data['person'] = person;
    }

    const body = data['body'] as RollbarBody | undefined;
    for (const trace of [body?.trace, ...(body?.trace_chain ?? [])]) {
        for (const frame of trace?.frames ?? []) {
            if (frame.filename) {
                frame.filename = toDynamicHostFilename(frame.filename, window.location);
            }
        }
    }
}

export const rollbar = new Rollbar({
    accessToken: (import.meta.env.VITE_ROLLBAR_TOKEN as string | undefined) ?? 'dev-no-token',
    captureUncaught: true,
    captureUnhandledRejections: true,
    environment: config.environment,
    enabled: Boolean(import.meta.env.VITE_ROLLBAR_TOKEN) && config.isProductionMode,
    scrubFields: SCRUB_FIELDS,
    scrubHeaders: SCRUB_HEADERS,
    checkIgnore: isExpectedError,
    transform: prepareRollbarItem,
    payload: {
        client: {
            javascript: {
                code_version: config.codeVersion,
                source_map_enabled: true,
                guess_uncaught_frames: true,
            },
        },
    },
});
